import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addSite,
  getMaintenanceToken,
  listSites,
  login,
  removeSite,
  setMaintenanceToken,
  updateSite
} from './services/maintenanceApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const absoluteApiBase = () => {
  if (/^https?:\/\//i.test(API_BASE_URL)) return API_BASE_URL;
  if (typeof window === 'undefined') return API_BASE_URL;
  return `${window.location.origin}${API_BASE_URL.startsWith('/') ? '' : '/'}${API_BASE_URL}`;
};

const statusUrlFor = (siteKey) =>
  `${absoluteApiBase()}/maintenance/status?site=${encodeURIComponent(siteKey)}`;

const WRENCH_PATH =
  'M30.031 23.631l-5.662-5.785c-0.369-0.369-0.985-0.369-1.354 0s-0.369 0.985 0 1.354l0.862 0.862-1.354 1.354c-0.369 0.369-0.369 0.985 0 1.354 0.123 0.123 0.369 0.246 0.615 0.246s0.492-0.123 0.615-0.246l1.354-1.354 2.831 2.831-3.815 3.815-6.892-6.892 12.062-12.062c0.369-0.369 0.369-0.985 0-1.354l-5.169-5.169c-0.123-0.123-0.369-0.246-0.615-0.246v0c-0.246 0-0.492 0.123-0.615 0.246l-6.892 7.015-7.631-7.631c-0.369-0.369-0.985-0.369-1.354 0l-5.046 5.169c-0.369 0.369-0.369 0.985 0 1.354l7.631 7.508-0.615 0.615c-0.369 0.369-0.369 0.985 0 1.354s0.985 0.369 1.354 0l9.477-9.477 3.815 3.815-11.938 12.062-4.554-4.677c-0.246-0.246-0.492-0.369-0.862-0.246-0.246 0.123-0.615 0.246-0.615 0.615l-3.2 8.369c-0.123 0.369 0 0.738 0.246 0.985 0.123 0.123 0.369 0.246 0.615 0.246 0.123 0 0.246 0 0.369 0l8.369-3.2c0 0 0 0 0 0 0.123 0 0.246-0.123 0.369-0.246l3.692-3.692 7.508 7.508c0.123 0.123 0.369 0.246 0.615 0.246s0.492-0.123 0.615-0.246l5.169-5.169c0.369-0.369 0.369-0.985 0-1.231zM10.831 14.769l-6.892-7.015 3.815-3.815 2.831 2.831-1.231 1.354c-0.369 0.369-0.369 0.985 0 1.354 0.123 0.123 0.369 0.246 0.615 0.246s0.492-0.123 0.615-0.246l1.354-1.354 2.831 2.831-3.938 3.815zM21.046 7.138l2.462-2.462 3.815 3.815-2.462 2.462-3.815-3.815zM4.923 27.077l1.969-5.046 3.077 3.2-5.046 1.846z';

/** The standard TMG maintenance page, as a full HTML document. */
const standalonePage = (messagePlaceholder) => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Under Maintenance</title>
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700&display=swap" rel="stylesheet">
  <style>
    body, html { margin:0; padding:0; height:100%; font-family:'DM Sans', sans-serif;
      background-color:#FFD200; display:flex; justify-content:center; align-items:center; }
    .maintenance-container { text-align:center; }
    .maintenance-container svg { width:64px; height:64px; fill:#333; margin-bottom:20px; }
    .maintenance-container h2 { font-size:28px; font-weight:600; color:#333; margin:0; }
    .maintenance-container p { font-size:16px; color:#333; margin:12px 0 0; }
  </style>
</head>
<body>
  <div class="maintenance-container">
    <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 32 32">
      <path d="${WRENCH_PATH}"></path>
    </svg>
    <h2>Under Maintenance</h2>
    ${messagePlaceholder}
  </div>
</body>
</html>`;

/** Same page as a full-screen overlay, for dropping into an existing page. */
const overlayMarkup = (messagePlaceholder) =>
  '<div style="position:fixed;inset:0;margin:0;padding:0;background-color:#FFD200;' +
  "font-family:'DM Sans',sans-serif;display:flex;justify-content:center;align-items:center;z-index:999999\">" +
  '<div style="text-align:center">' +
  '<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 32 32" ' +
  'style="width:64px;height:64px;fill:#333;margin-bottom:20px"><path d="' + WRENCH_PATH + '"></path></svg>' +
  '<h2 style="font-size:28px;font-weight:600;color:#333;margin:0">Under Maintenance</h2>' +
  messagePlaceholder +
  '</div></div>';

const snippetsFor = (siteKey) => {
  const url = statusUrlFor(siteKey);
  return [
    {
      id: 'html',
      label: 'Any website (HTML)',
      hint: 'Paste just before </body>. Shows a full-screen maintenance overlay when the switch is on.',
      code: `<script>
(function () {
  var OVERLAY = ${JSON.stringify(overlayMarkup('{{MESSAGE}}'))};
  fetch("${url}")
    .then(function (r) { return r.json(); })
    .then(function (res) {
      if (!res.data || !res.data.is_down) return;
      if (new URLSearchParams(location.search).get("bypass") === "1") return;

      var font = document.createElement("link");
      font.rel = "stylesheet";
      font.href = "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700&display=swap";
      document.head.appendChild(font);
      document.title = "Under Maintenance";

      var msg = res.data.message
        ? '<p style="font-size:16px;color:#333;margin:12px 0 0">' + res.data.message + '</p>'
        : '';
      document.body.innerHTML = OVERLAY.replace('{{MESSAGE}}', msg);
    })
    .catch(function () { /* if the switch is unreachable, leave the site up */ });
})();
</script>`
    },
    {
      id: 'node',
      label: 'Node / Express',
      hint: 'Add as middleware before your routes. Caches for 10s so it does not call out on every request.',
      code: `// maintenance.js
const PAGE = ${JSON.stringify(standalonePage('{{MESSAGE}}'))};

let cache = { at: 0, down: false, message: null };

module.exports = async function maintenance(req, res, next) {
  try {
    if (Date.now() - cache.at > 10000) {
      const r = await fetch("${url}");
      const { data } = await r.json();
      cache = { at: Date.now(), down: !!data.is_down, message: data.message };
    }
  } catch (e) { /* unreachable switch -> stay up */ }

  if (!cache.down) return next();
  const body = PAGE.replace('{{MESSAGE}}', cache.message ? '<p>' + cache.message + '</p>' : '');
  res.status(503).type('html').send(body);
};`
    },
    {
      id: 'php',
      label: 'PHP / WordPress',
      hint: 'Drop into your theme functions.php or a small mu-plugin.',
      code: `<?php
$cache = sys_get_temp_dir() . '/maint_${siteKey}.json';
if (!file_exists($cache) || time() - filemtime($cache) > 10) {
  $raw = @file_get_contents("${url}");
  if ($raw !== false) { @file_put_contents($cache, $raw); }
}
$res = @json_decode(@file_get_contents($cache), true);
if (!empty($res['data']['is_down'])) {
  http_response_code(503);
  $msg = htmlspecialchars($res['data']['message'] ?? '');
  $page = ${JSON.stringify(standalonePage('{{MESSAGE}}'))};
  exit(str_replace('{{MESSAGE}}', $msg ? "<p>$msg</p>" : '', $page));
}`
    }
  ];
};

const CopyButton = ({ value, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const el = document.createElement('textarea');
      el.value = value;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 whitespace-nowrap"
    >
      {copied ? 'Copied' : label}
    </button>
  );
};

const SiteCard = ({ site, isCurrent, onToggle, onMessage, onDelete, busy }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(site.message || '');
  const [tab, setTab] = useState('html');
  const snippets = useMemo(() => snippetsFor(site.site_key), [site.site_key]);
  const snippet = snippets.find((s) => s.id === tab) || snippets[0];

  useEffect(() => {
    setMessage(site.message || '');
  }, [site.message]);

  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
      <div className="p-4 flex flex-wrap items-center gap-3">
        <span
          className={`inline-flex h-2.5 w-2.5 rounded-full ${site.is_down ? 'bg-red-500' : 'bg-green-500'}`}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-[180px]">
          <div className="font-semibold text-gray-900">
            {site.name}
            {isCurrent && (
              <span className="ml-2 text-[11px] font-medium text-gray-500 border border-gray-300 rounded px-1.5 py-0.5">
                this site
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 font-mono">{site.site_key}</div>
        </div>

        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
            site.is_down ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}
        >
          {site.is_down ? 'DOWN' : 'UP'}
        </span>

        <button
          type="button"
          disabled={busy}
          onClick={() => onToggle(site, !site.is_down)}
          className={`px-4 py-2 text-sm font-semibold rounded-lg disabled:opacity-50 ${
            site.is_down
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-red-600 text-white hover:bg-red-700'
          }`}
        >
          {site.is_down ? 'Bring back up' : 'Take down'}
        </button>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
        >
          {open ? 'Hide' : 'Connect'}
        </button>
      </div>

      {open && (
        <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-1">Message shown to visitors (optional)</div>
            <div className="flex gap-2">
              <input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Back by 9pm - upgrading the claim system"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => onMessage(site, message)}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-black disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-700 mb-1">Status endpoint for this site</div>
            <div className="flex gap-2 items-center">
              <code className="flex-1 text-xs bg-white border border-gray-200 rounded-lg px-3 py-2 overflow-x-auto whitespace-nowrap">
                {statusUrlFor(site.site_key)}
              </code>
              <CopyButton value={statusUrlFor(site.site_key)} label="Copy URL" />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Returns <code>{'{ "data": { "is_down": true|false, "message": ... } }'}</code>. No password needed to read it.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
              <div className="flex gap-1">
                {snippets.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setTab(s.id)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
                      tab === s.id
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <CopyButton value={snippet.code} label="Copy snippet" />
            </div>
            <p className="text-xs text-gray-500 mb-2">{snippet.hint}</p>
            <pre className="text-xs bg-gray-900 text-gray-100 rounded-lg p-3 overflow-x-auto">
              <code>{snippet.code}</code>
            </pre>
          </div>

          {!isCurrent && (
            <button
              type="button"
              onClick={() => onDelete(site)}
              className="text-xs text-red-600 hover:text-red-700 underline"
            >
              Remove this site
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default function MaintenanceAdmin() {
  const [authed, setAuthed] = useState(Boolean(getMaintenanceToken()));
  const [password, setPassword] = useState('');
  const [sites, setSites] = useState([]);
  const [currentKey, setCurrentKey] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newKey, setNewKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSites();
      setSites(data.sites);
      setCurrentKey(data.current_site_key);
      setError('');
    } catch (e) {
      if (/session expired/i.test(e.message)) setAuthed(false);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(password);
      setPassword('');
      setAuthed(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      if (/session expired/i.test(e.message)) setAuthed(false);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = (site, nextDown) => {
    if (nextDown && !window.confirm(`Take "${site.name}" down for maintenance?`)) return;
    act(() => updateSite(site.site_key, { is_down: nextDown }));
  };

  const handleAdd = (e) => {
    e.preventDefault();
    const key = (newKey || newName).trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-');
    if (!key) return;
    act(async () => {
      await addSite(key, newName.trim() || key);
      setNewName('');
      setNewKey('');
    });
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <form onSubmit={doLogin} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 w-full max-w-sm">
          <h1 className="text-lg font-bold text-gray-900">Maintenance Control</h1>
          <p className="text-sm text-gray-500 mt-1 mb-4">Enter the maintenance password to continue.</p>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          <button
            type="submit"
            disabled={busy || !password}
            className="mt-4 w-full py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-black disabled:opacity-50"
          >
            {busy ? 'Checking...' : 'Unlock'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
      <div className="max-w-3xl mx-auto space-y-5">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Maintenance Control</h1>
            <p className="text-sm text-gray-500">
              Flip any connected site into maintenance mode. Changes take effect within ~10 seconds.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setMaintenanceToken(null);
              setAuthed(false);
            }}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Lock
          </button>
        </header>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
        )}

        <div className="space-y-3">
          {loading && sites.length === 0 && <div className="text-sm text-gray-500">Loading...</div>}
          {sites.map((site) => (
            <SiteCard
              key={site.site_key}
              site={site}
              isCurrent={site.site_key === currentKey}
              busy={busy}
              onToggle={handleToggle}
              onMessage={(s, message) => act(() => updateSite(s.site_key, { message }))}
              onDelete={(s) =>
                window.confirm(`Remove "${s.name}" from the list?`) &&
                act(() => removeSite(s.site_key))
              }
            />
          ))}
        </div>

        <form onSubmit={handleAdd} className="bg-white border border-gray-200 rounded-xl p-4">
          <h2 className="font-semibold text-gray-900 mb-1">Add a website</h2>
          <p className="text-xs text-gray-500 mb-3">
            Add it here, then open <strong>Connect</strong> on the new card to copy its endpoint and snippet.
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Website name (e.g. TMG Quotes)"
              className="flex-1 min-w-[180px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
            <input
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="key (optional, e.g. quotes)"
              className="w-44 px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
            <button
              type="submit"
              disabled={busy || !newName.trim()}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-yellow-400 text-gray-900 hover:bg-yellow-500 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
